<section>
  <h1 class="mb-3">People</h1>
  <a *removeIfUnauthorized="canCreate" (click)="newPerson()" class="btn btn-active mb-3">Add person</a>
  <div class="card-group mb-4">
    <div class="card">
      <div #tgt id="filterBody" class="card-body">
        <h2 class="card-title h5">RMS People List</h2>
        <p class="mb-2 card-text font-size-sm">View a list of people filtered by event date range<ng-container *ngIf="!isStateLocalLE"> and geographic region of the agency investigating the associated RMS event(s)</ng-container>.</p>
        <p *ngIf="!isStateLocalLE" class="mb-2 card-text font-size-sm">Default geographic filter preferences can be set in your <a [routerLink]="['/account/userprofile']">user profile</a>.</p>
        <ness-date-range-filter #df [suppressUI]="true" [dateFilterStorageKey]="peopleStorageKey"></ness-date-range-filter>
        <ng-container *removeIfNoPermission="canView">
          <ness-geographic-filter #gf [suppressUI]="true" [geoFilterStorageKey]="geoFilterStorageKey"  (isLoading)="loading = $event"></ness-geographic-filter>
        </ng-container>
        <div class="mt-3 d-flex align-items-start flex-wrap flex-md-nowrap">
          <div class="font-size-sm flex-grow-1 me-3 mb-2"><i class="fas fa-info-circle text-active" aria-hidden="true"></i> People not associated with an RMS event in NESS will not be included in the filter results.</div>
          <button type="button" class="btn btn-active btn-sm text-nowrap mb-2" [disabled]="loading" [attr.aria-disabled]="loading" (click)="applyFilters()" title="Apply filters">View List<span class="visually-hidden"> filters</span></button>
          <button type="button" class="btn btn-secondary btn-sm text-nowrap mb-2" [disabled]="loading" [attr.aria-disabled]="loading" (click)="resetDefaultFilters()" title="Reset filters to defaults">Reset<span class="visually-hidden"> filters to defaults</span></button>
        </div>
      </div>
    </div>
    <div class="align-self-center text-center my-3 mx-4" style="width: 2rem;"><span class="visually-hidden">Filter people by event date range and investigating agency region,</span> <span class="fw-bold">OR</span> <span class="visually-hidden">search for people using selected criteria.</span></div>
    <div class="card">
      <div class="card-body">
        <h2 class="h5 card-title">RMS People Search</h2>
        <p class="mb-2 card-text font-size-sm">Search for any person in NESS based on the selected criteria. This will return all matching people records in NESS.</p>
        <ness-person-search (showSearchResults)="toggleSearchGrid($event)"></ness-person-search>
      </div>
    </div>
  </div>
  <p-messages key="peopleMsgs" [closable]="true"></p-messages>
  <ng-container *ngIf="showSearchResults">
    <ness-person-search-results (viewPersonIDDetails)="viewPersonDetails($event)"></ness-person-search-results>
  </ng-container>
  <ng-container *ngIf="showFilterResults">
    <p-table #dt [columns]="filterResultsCols" [value]="people" [paginator]="true" [alwaysShowPaginator]="false" [rows]="rows" [autoLayout]="true" responsiveLayout="scroll">
      <ng-template pTemplate="caption">
        <ness-paged-loading-status *ngIf="loading" [startRow]="loadingStartRow$ | async" [endRow]="loadingEndRow$ | async" [messageTemplate]="messageTemplate"></ness-paged-loading-status>
        <div class="d-flex flex-wrap p-2 mx-2 filter-status font-size-sm">
          <div class="p-2 me-2">Showing:</div>
          <ness-date-range-filter-status *ngIf="!showAllPeople"></ness-date-range-filter-status>
          <ng-container *removeIfNoPermission="canView">
            <ness-geographic-filter-status *ngIf="!showAllPeople"></ness-geographic-filter-status>
          </ng-container>
          <div *ngIf="showAllPeople" class="ness-filter-tag p-2 me-2 mb-2">All people</div>
          <button type="button" class="btn btn-success mb-2 me-2" [disabled]="loading || showAllPeople" [attr.aria-disabled]="loading || showAllPeople" title="Clear applied filters" (click)="clearAppliedFilters()">Clear <span class="visually-hidden">applied filters</span></button>
          <div class="ms-md-2 ms-lg-auto me-2">
            <div class="input-group">
              <span class="input-group-text"><i class="fa fa-search" aria-hidden="true"></i></span>
              <input type="text" class="form-control" name="peopleGridGlobalFilter" placeholder="People grid filter" (input)="dt.filterGlobal($event['target']['value'], 'contains')" aria-label="Globally filter the people grid">
            </div>
          </div>
        </div>
      </ng-template>
      <ng-template pTemplate="header" let-columns>
        <tr>
          <th scope="col" *ngFor="let col of columns" [pSortableColumn]="col.field">
            {{col.header}}
            <p-sortIcon [field]="col.field" ariaLabel="Activate to sort" ariaLabelDesc="Activate to sort in descending order" ariaLabelAsc="Activate to sort in ascending order"></p-sortIcon>
          </th>
        </tr>
        <tr>
          <th scope="col" *ngFor="let col of columns" [ngSwitch]="col.field">
            <label *ngSwitchCase="!'DateOfBirth' && !'RelatedEvents'" class="visually-hidden" for="filterBy{{col.field}}">Filter by {{col.header}} column</label>
            <input *ngSwitchCase="'FirstName' || 'LastName'" id="filterBy{{col.field}}" class="form-control form-control-sm" type="text" tabindex="0" (input)="dt.filter($event['target']['value'], col.field, col.filterMatchMode)">
            <input *ngSwitchCase="'SSN'" id="filterBy{{col.field}}" class="form-control form-control-sm" type="text" [(ngModel)]="ssnSortValue" (keydown)="ssnFilterEvent($event)" (input)="dt.filter($event['target']['value'], col.field, col.filterMatchMode)" maxlength="11" tabindex="0">
            <span *ngSwitchCase="'DateOfBirth' || 'RelatedEvents'" class="visually-hidden">(No filter available for {{col.field}} column)</span>
            <input *ngSwitchDefault id="filterBy{{col.field}}" class="form-control form-control-sm" type="text" tabindex="0" (input)="dt.filter($event['target']['value'], col.field, col.filterMatchMode)">
          </th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-rowData let-columns="columns">
        <tr [pSelectableRow]="rowData">
          <ng-container *ngFor="let col of columns">
            <th scope="row" *ngIf="col.header==='Last name';else tddefault"><button class="btn btn-link" aria-label="{{'View person '+rowData.FirstName+' '+rowData.LastName+' details'}}" tabindex="0" (click)="viewPersonDetails(rowData.PersonID)">{{rowData[col.field]}}</button></th>
            <ng-template #tddefault>
              <td [ngSwitch]="col.field">
                <button *ngSwitchCase="'LastName'" type="button" class="btn btn-sm btn-link px-0" aria-label="{{'Person '+ rowData.LastName +' details'}}" tabindex="0" (click)="viewPersonDetails(rowData.PersonID)">{{ rowData.LastName }}</button>
                <ng-container *ngSwitchCase="'DateOfBirth'">{{ rowData.DateOfBirth | date:"MM/dd/yyyy" }}</ng-container>
                <ng-container *ngSwitchCase="'SSN'">{{ rowData.SSN | ssnFormat }}</ng-container>
                <ng-container *ngSwitchCase="'MostRecentEventDate'">{{ rowData.MostRecentEventDate | date:"MM/dd/yyyy" }}</ng-container>
                <ng-container *ngSwitchDefault>{{rowData[col.field]}}</ng-container>
              </td>
            </ng-template>
          </ng-container>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage" let-columns>
        <tr>
          <td [attr.colspan]="columns.length">
            No person found.
          </td>
        </tr>
      </ng-template>
    </p-table>
  </ng-container>
</section>
<p-sidebar (onShow)="onShowSidebar()" (onHide)="onViewPersonDetailsClose()" [(visible)]="displaySidebar" position="right" [blockScroll]="true" styleClass="p-sidebar-lg" [showCloseIcon]="false">
  <ng-container *ngIf="openViewPersonDetailForm">
    <ng-container *ngIf="personDTO$ | async as person; else loadingPerson">
      <ness-person-detail #personView [personDTO]="person" [isEventPersonForm]="false" [suppressEdit]="false" [hasDeletePermission]="hasDeletePermission$ | async" (deletePersonCompleted)="deletePersonCompleted($event)" (showPersonAddressDetail)="viewAddressDetails($event)"></ness-person-detail>
    </ng-container>
  </ng-container>
  <ng-template #loadingPerson>
    <p-messages #viewPersonDetailMsg key="viewPersonDetailMsg" [closable]="true"></p-messages>
    <div class="m-3">Loading person...</div>
  </ng-template>
  <ng-container *ngIf="openViewPersonAddressDetails">
    <ng-container *ngIf="personAddress; else loadingAddress">
      <ness-location-detail *ngIf="showLocationView" [addressDTO]="personAddress" [hasDeletePermission]="false" [hasEditPermission]="hasPermissionToEditAddress" [personAddressType]="personAddressType" [isFromPersonDetails]="true" (editLocationLocationClicked)="editLocation($event)"></ness-location-detail>
    </ng-container>
  </ng-container>
  <button *ngIf="!openViewPersonAddressDetailForm" type="button" (click)="onClickCloseButton()" class="btn btn-active m-3">Close</button>
  <ng-container *ngIf="openViewPersonAddressDetailForm">
    <ng-container *ngIf="personAddress; else loadingAddress">
      <ness-location-form *ngIf="showEditLocationView" [titleText]="'Edit location'" [isEventLocationForm]="false" [isEditForm]="true" [locationFormData]="personAddress" (locationSaved)="onLocationFormClose($event)" (closeForm)="onLocationFormClose(personAddress)"></ness-location-form>
    </ng-container>
  </ng-container>
  <ng-template #loadingAddress>
    <p-messages #viewPersonDetailMsg key="viewAddressDetailsMsg" [closable]="true"></p-messages>
    <div class="m-3">Loading location...</div>
  </ng-template>
</p-sidebar>

